import { useMemo, useState } from "react";
import { ArrowUpDown } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Structure } from "@/types/designs";
import { DesignToggleField } from "./DesignTableCell";
type PortCombosTableProps = {
  structure: Structure;
  isEditing?: boolean;
  showDefaults?: boolean;
};

type PortComboSortKey =
  | "name"
  | "index"
  | "inputs"
  | "outputs"
  | "pads"
  | "settings"
  | "alignAtPeak"
  | "alignPwrRng"
  | "measurePwrRng"
  | "zMargin"
  | "probeCheck"
  | "blindStep"
  | "dynamicAlignment";

type SortDirection = "asc" | "desc";

type VisibleColumn = {
  key: PortComboSortKey;
  label: string;
  widthClassName: string;
  isVisible?: boolean;
};

const getComparableValue = (value: string | number | boolean | undefined) => {
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "number") return value;
  return String(value ?? "")
    .trim()
    .toLowerCase();
};

export function PortCombosTable({
  structure,
  isEditing = false,
  showDefaults = false,
}: PortCombosTableProps) {
  const [sortState, setSortState] = useState<{
    key: PortComboSortKey;
    direction: SortDirection;
  } | null>(null);

  const sortedRows = useMemo(() => {
    const rows = structure.portCombos ?? [];

    if (!sortState) return rows;

    const { key, direction } = sortState;
    const multiplier = direction === "asc" ? 1 : -1;

    return [...rows].sort((a, b) => {
      const leftValue = getComparableValue(
        (a as Record<string, string | number | boolean | undefined>)[key],
      );
      const rightValue = getComparableValue(
        (b as Record<string, string | number | boolean | undefined>)[key],
      );

      if (typeof leftValue === "number" && typeof rightValue === "number") {
        return (leftValue - rightValue) * multiplier;
      }

      return String(leftValue).localeCompare(String(rightValue)) * multiplier;
    });
  }, [sortState, structure.portCombos]);

  const handleSort = (key: PortComboSortKey) => {
    setSortState((current) => {
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }

      return { key, direction: "asc" };
    });
  };

  const renderSortIcon = (key: PortComboSortKey) => {
    if (sortState?.key !== key) {
      return <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />;
    }

    return <span className="text-xs font-medium">{sortState.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const columns: VisibleColumn[] = [
    { key: "name", label: "Name", widthClassName: "w-[140px]" },
    { key: "index", label: "Index", widthClassName: "w-[90px]" },
    { key: "inputs", label: "Inputs", widthClassName: "w-[150px]" },
    { key: "outputs", label: "Outputs", widthClassName: "w-[150px]" },
    { key: "pads", label: "Pads", widthClassName: "w-[110px]" },
    { key: "settings", label: "Settings", widthClassName: "w-[150px]" },
    {
      key: "alignAtPeak",
      label: "Align At Peak",
      widthClassName: "w-[150px]",
      isVisible: showDefaults,
    },
    {
      key: "alignPwrRng",
      label: "Align Pwr Rng",
      widthClassName: "w-[150px]",
      isVisible: showDefaults,
    },
    {
      key: "measurePwrRng",
      label: "Measure Pwr Rng",
      widthClassName: "w-[170px]",
      isVisible: showDefaults,
    },
    {
      key: "zMargin",
      label: "Z Margin",
      widthClassName: "w-[120px]",
      isVisible: showDefaults,
    },
    {
      key: "probeCheck",
      label: "Probe Check",
      widthClassName: "w-[130px]",
      isVisible: showDefaults,
    },
    {
      key: "blindStep",
      label: "Blind Step",
      widthClassName: "w-[130px]",
      isVisible: showDefaults,
    },
    {
      key: "dynamicAlignment",
      label: "Dynamic Alignment",
      widthClassName: "w-[170px]",
      isVisible: showDefaults,
    },
  ];

  const visibleColumns = columns.filter((column) => column.isVisible !== false);

  return (
    <div className="m-0">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            {visibleColumns.map((column) => (
              <TableHead key={column.key} className={column.widthClassName}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort(column.key)}
                >
                  {column.label} {renderSortIcon(column.key)}
                </button>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.length > 0 ? (
            sortedRows.map((combo) => (
              <TableRow key={combo.id}>
                {visibleColumns.map((column) => (
                  <TableCell key={column.key} className={column.widthClassName}>
                    <DesignToggleField value={combo[column.key]} isEditing={isEditing} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length}
                className="h-24 text-center text-muted-foreground"
              >
                No port combos available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
